import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import FileUploadResult from "../classes/FileUploadResult";
import ResultStatus from "../classes/ResultStatus";
import LabelWithInputField from "./LabelWithInputField";
import FileToUpload from "../classes/FileToUpload";
import PageNavigator from "../classes/PageNavigator";
import ServiceCategory from "../models/ServiceCategory";
import Result from "../classes/Result";
import Button from "./Button";
import DBSaveStatus from "../utils/DBSaveStatus";
import CheckboxWithText from "./CheckboxWithText";
import PlatformClient from "../classes/PlatformClient";
import Browser from "../classes/Browser";
import TextView from "./TextView";
import Divider from "./Divider";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _AddImageOnPressed = (d3eState: AddOrEditServiceCategoryViewRefs) => void;

type _CancelOnPressed = (d3eState: AddOrEditServiceCategoryViewRefs) => void;

type _SaveButtonOnPressed = (
  d3eState: AddOrEditServiceCategoryViewRefs
) => void;

type _NameFieldOnChanged = (
  text: string,
  d3eState: AddOrEditServiceCategoryViewRefs
) => void;

type _CheckBoxInCallOnChanged = (
  text: boolean,
  d3eState: AddOrEditServiceCategoryViewRefs
) => void;

type _CheckBoxOutCallOnChanged = (
  text: boolean,
  d3eState: AddOrEditServiceCategoryViewRefs
) => void;

type _CheckBoxTeleCallOnChanged = (
  text: boolean,
  d3eState: AddOrEditServiceCategoryViewRefs
) => void;

export interface AddOrEditServiceCategoryViewProps extends BaseUIProps {
  key?: string;
  serviceCategory: ServiceCategory;
}
/// To store state data for AddOrEditServiceCategoryView
class AddOrEditServiceCategoryViewRefs {
  public addImage: AddImageState = new AddImageState();
  public cancel: CancelState = new CancelState();
  public saveButton: SaveButtonState = new SaveButtonState();
}

interface SaveButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditServiceCategoryViewRefs;
  _onPressedSaveButtonHandler?: _SaveButtonOnPressed;
}

class SaveButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _SaveButtonWithState extends ObservableComponent<SaveButtonWithStateProps> {
  saveButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SaveButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get saveButton(): SaveButtonState {
    return this.props.d3eState.saveButton;
  }
  public get d3eState(): AddOrEditServiceCategoryViewRefs {
    return this.props.d3eState;
  }
  public get _onPressedSaveButtonHandler(): _SaveButtonOnPressed {
    return this.props._onPressedSaveButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("saveButton", null, this.saveButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["saveButton", "saveButton.", "saveButton.hover"], this.rebuild);
  }
  public saveButtonOnEnter(event): void {
    return this.saveButton.setHover(true);
  }
  public saveButtonOnExit(event): void {
    return this.saveButton.setHover(false);
  }
  public dispose(): void {
    this.saveButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: this.saveButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.saveButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.saveButton.disable,
        onPressed: () => {
          this._onPressedSaveButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.saveButtonOnEnter(event);
        },
        onExit: (event) => {
          this.saveButtonOnExit(event);
        },
      }),
      className: "xb4",
    });
  }
}
function SaveButtonWithState(props: SaveButtonWithStateProps) {
  return React.createElement(_SaveButtonWithState, props);
}

interface CancelWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditServiceCategoryViewRefs;
  _onCancelButtonClicked?: _CancelOnPressed;
}

class CancelState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CancelWithState extends ObservableComponent<CancelWithStateProps> {
  cancelFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancel(): CancelState {
    return this.props.d3eState.cancel;
  }
  public get d3eState(): AddOrEditServiceCategoryViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelButtonClicked(): _CancelOnPressed {
    return this.props._onCancelButtonClicked;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancel", null, this.cancel);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["cancel", "cancel.", "cancel.hover"], this.rebuild);
  }
  public cancelOnEnter(event): void {
    return this.cancel.setHover(true);
  }
  public cancelOnExit(event): void {
    return this.cancel.setHover(false);
  }
  public dispose(): void {
    this.cancel.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: this.cancel.hover
          ? cStyle.tButtonPrimaryOutlinePaddingOnHover
          : cStyle.tButtonPrimaryOutlinePaddingOn,
        decoration: this.cancel.hover
          ? cStyle.tButtonPrimaryOutlineDecorationOnHover
          : cStyle.tButtonPrimaryOutlineDecorationOn,
        disable: this.cancel.disable,
        onPressed: () => {
          this._onCancelButtonClicked(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelOnEnter(event);
        },
        onExit: (event) => {
          this.cancelOnExit(event);
        },
      }),
      className: "x45",
    });
  }
}
function CancelWithState(props: CancelWithStateProps) {
  return React.createElement(_CancelWithState, props);
}

interface AddImageWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditServiceCategoryViewRefs;
  _onPresseProfileButtonHandler?: _AddImageOnPressed;
  serviceCategory: ServiceCategory;
}

class AddImageState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _AddImageWithState extends ObservableComponent<AddImageWithStateProps> {
  addImageFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddImageWithStateProps) {
    super(props);

    this.initState();
  }
  public get addImage(): AddImageState {
    return this.props.d3eState.addImage;
  }
  public get serviceCategory(): ServiceCategory {
    return this.props.serviceCategory;
  }
  public get d3eState(): AddOrEditServiceCategoryViewRefs {
    return this.props.d3eState;
  }
  public get _onPresseProfileButtonHandler(): _AddImageOnPressed {
    return this.props._onPresseProfileButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("addImage", null, this.addImage);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("serviceCategory", this.props.serviceCategory);

    this.on(
      ["addImage", "serviceCategory", "serviceCategory.image"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AddImageWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.serviceCategory !== this.props.serviceCategory) {
      this.updateObservable(
        "serviceCategory",
        prevProps.serviceCategory,
        this.props.serviceCategory
      );

      this.fire("serviceCategory", this);
    }
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 10.0, new Map()),
      child: Button({
        disable: this.addImage.disable,
        onPressed: () => {
          this._onPresseProfileButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Container({
          alignment: ui.Alignment.center,
          child:
            this.serviceCategory.image === null
              ? ui.AssetImage({
                  path: "images/EmptyImage.png",
                  height: 71,
                  width: 71,
                  fit: ui.BoxFit.fill,
                  className: "x77 hc vc",
                })
              : ui.ClipRRect({
                  borderRadius: ui.BorderRadius.circular(50),
                  child: ui.NetworkImage({
                    url: this.serviceCategory.image.downloadUrl,
                    height: 100,
                    width: 100,
                    fit: ui.BoxFit.fill,
                    className: "x2b hc vc",
                  }),
                }),
          className: "x7d hc vc",
        }),
      }),
      className: "xe4 hc",
    });
  }
}
function AddImageWithState(props: AddImageWithStateProps) {
  return React.createElement(_AddImageWithState, props);
}

class _AddOrEditServiceCategoryViewState extends ObservableComponent<AddOrEditServiceCategoryViewProps> {
  static defaultProps = { serviceCategory: null };
  d3eState: AddOrEditServiceCategoryViewRefs =
    new AddOrEditServiceCategoryViewRefs();
  isSavedObj: boolean = false;
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _nameErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddOrEditServiceCategoryViewProps) {
    super(props);

    this.initState();
  }
  public get serviceCategory(): ServiceCategory {
    return this.props.serviceCategory;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("serviceCategory", this.props.serviceCategory);

    this.on(["serviceCategory"], this.computeIsSavedObj);

    this.computeIsSavedObj();

    this.on(
      [
        "isSavedObj",
        "nameErrors",
        "serviceCategory",
        "serviceCategory.inCall",
        "serviceCategory.name",
        "serviceCategory.outCall",
        "serviceCategory.teleCall",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(
    prevProps: AddOrEditServiceCategoryViewProps
  ): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.serviceCategory !== this.props.serviceCategory) {
      this.updateObservable(
        "serviceCategory",
        prevProps.serviceCategory,
        this.props.serviceCategory
      );

      this.fire("serviceCategory", this);
    }
  }
  public setIsSavedObj(val: boolean): void {
    let isValChanged: boolean = this.isSavedObj !== val;

    if (!isValChanged) {
      return;
    }

    this.isSavedObj = val;

    this.fire("isSavedObj", this);
  }
  public computeIsSavedObj = (): void => {
    try {
      this.setIsSavedObj(this.serviceCategory.saveStatus !== DBSaveStatus.New);
    } catch (exception) {
      console.log(" exception in computeIsSavedObj : " + exception.toString());

      this.setIsSavedObj(false);
    }
  };
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get nameErrors(): Array<string> {
    return this._nameErrors ?? [];
  }
  public set nameErrors(val: Array<string>) {
    this._nameErrors.clear();

    this._nameErrors.addAll(val);
  }
  public async computeNameErrors(): Promise<void> {
    this.nameErrors = [];

    let it: string = this.serviceCategory.name;

    if (it == null || it === "") {
      this.nameErrors.add("Name cannot be empty.");
    }

    this.fire("nameErrors", this);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.nameErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeNameErrors();

    errors.addAll(this.nameErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisSize: ui.MainAxisSize.min,
      children: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              children: [
                TextView({
                  data: this.isSavedObj
                    ? "Edit Service Category"
                    : "Add Service Category",
                  style: new ui.TextStyle({
                    fontSize: 20,
                    fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                  }),
                  className: "xf81",
                  key: "0",
                }),
              ],
              className: "x89 hc h",
              key: "0",
            }),
            Divider({ className: "xbe hc h", key: "1" }),
            ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                AddImageWithState({
                  d3eState: this.d3eState,
                  _onPresseProfileButtonHandler:
                    this.onPresseProfileButtonHandler,
                  serviceCategory: this.serviceCategory,
                  key: "0",
                }),
                LabelWithInputField({
                  name: "Name",
                  value: this.serviceCategory.name,
                  errors: this.nameErrors,
                  isRequired: true,
                  placeHolder: "Enter name",
                  onChanged: (text) => {
                    this.nameFieldonChanged(text, this.d3eState);
                  },
                  className: "x99 hc h",
                  key: "1",
                }),
                TextView({
                  data: "Service Availability",
                  style: new ui.TextStyle({
                    color: cStyle.tTextViewLabelTextColorOn,
                    fontSize: cStyle.tTextViewLabelTextFontSizeOn,
                    fontWeight: cStyle.tTextViewLabelTextFontWeightOn,
                  }),
                  className: "x9e hc",
                  key: "2",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.spaceEvenly,
                  children: [
                    ui.Container({
                      margin: ui.EdgeInsets.fromLTRB(
                        0.0,
                        0.0,
                        10.0,
                        0.0,
                        new Map()
                      ),
                      child: CheckboxWithText({
                        value: this.serviceCategory.inCall,
                        title: "In Call",
                        onChanged: (text) => {
                          this.checkBoxInCallonChanged(text, this.d3eState);
                        },
                      }),
                      key: "0",
                      className: "x96",
                    }),
                    ui.Container({
                      margin: ui.EdgeInsets.fromLTRB(
                        0.0,
                        0.0,
                        10.0,
                        0.0,
                        new Map()
                      ),
                      child: CheckboxWithText({
                        value: this.serviceCategory.outCall,
                        title: "Out Call",
                        onChanged: (text) => {
                          this.checkBoxOutCallonChanged(text, this.d3eState);
                        },
                      }),
                      key: "1",
                      className: "x06",
                    }),
                    CheckboxWithText({
                      value: this.serviceCategory.teleCall,
                      title: "Tele Call",
                      onChanged: (text) => {
                        this.checkBoxTeleCallonChanged(text, this.d3eState);
                      },
                      key: "2",
                    }),
                  ],
                  className: "x7f hc h",
                  key: "3",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
                  children: [
                    CancelWithState({
                      d3eState: this.d3eState,
                      _onCancelButtonClicked: this.onCancelButtonClicked,
                      key: "0",
                    }),
                    SaveButtonWithState({
                      d3eState: this.d3eState,
                      _onPressedSaveButtonHandler:
                        this.onPressedSaveButtonHandler,
                      key: "1",
                    }),
                  ],
                  className: "xba hc h",
                  key: "4",
                }),
              ],
              className: "x82 hc h",
              key: "2",
            }),
          ],
          className: "x01 hc h",
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AddOrEditServiceCategoryView hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onPresseProfileButtonHandler = async (
    d3eState: AddOrEditServiceCategoryViewRefs
  ): Promise<void> => {
    let fileToUpload: FileToUpload = await Browser.selectFile(
      ["jpg", "jpeg", "png"],
      () => {}
    );

    if (fileToUpload !== null) {
      let res: FileUploadResult = await PlatformClient.upload(fileToUpload);

      if (res.success) {
        this.serviceCategory.setImage(res.file);
      }
    }
  };
  public onPressedSaveButtonHandler = async (
    d3eState: AddOrEditServiceCategoryViewRefs
  ): Promise<void> => {
    if (this.validate().isEmpty) {
      let result: Result<ServiceCategory> = await this.serviceCategory.save();

      if (result.status === ResultStatus.Success) {
        this.navigator.close();
      }
    }
  };
  public onCancelButtonClicked = (
    d3eState: AddOrEditServiceCategoryViewRefs
  ): void => {
    this.serviceCategory.restore();

    this.navigator.close();
  };
  public nameFieldonChanged = (
    val: string,
    d3eState: AddOrEditServiceCategoryViewRefs
  ): void => {
    this.serviceCategory.setName(val);
  };
  public checkBoxInCallonChanged = (
    val: boolean,
    d3eState: AddOrEditServiceCategoryViewRefs
  ): void => {
    this.serviceCategory.setInCall(val);
  };
  public checkBoxOutCallonChanged = (
    val: boolean,
    d3eState: AddOrEditServiceCategoryViewRefs
  ): void => {
    this.serviceCategory.setOutCall(val);
  };
  public checkBoxTeleCallonChanged = (
    val: boolean,
    d3eState: AddOrEditServiceCategoryViewRefs
  ): void => {
    this.serviceCategory.setTeleCall(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get addImage() {
    return this.d3eState.addImage;
  }
  public get cancel() {
    return this.d3eState.cancel;
  }
  public get saveButton() {
    return this.d3eState.saveButton;
  }
}
export default function AddOrEditServiceCategoryView(
  props: AddOrEditServiceCategoryViewProps
) {
  return React.createElement(_AddOrEditServiceCategoryViewState, {
    ..._AddOrEditServiceCategoryViewState.defaultProps,
    ...props,
  });
}
